/**
 * Workflow Definition Writer
 *
 * @module @chatsdet/diagnostic-workspace/workflow-writer
 * @description Writes `workflow/definition.json`, `workflow/snapshot.json`, and one
 * `workflow/nodes/<nodeId>.json` per node. The agent reads `workflow/nodes/<nodeId>.json`
 * for the full configJson when comparing what a step was supposed to do vs what actually
 * happened, and when proposing `configPatch` suggestions.
 */

import * as fs from 'node:fs/promises';
import { topoOrderNodes } from '@chatsdet/workflow-diff';
import {
  resolveWorkspacePath,
  workflowNodeRelativePath,
  WORKSPACE_PATHS,
} from './paths.js';

export interface WorkflowWriterNode {
  id: string;
  nodeType: string;
  configJson: unknown;
  orderIndex: number;
  /** Pre-resolved one-line summary (caller computes via getNodeSummary). */
  summary: string;
}

export interface WorkflowWriterEdge {
  id: string;
  source: string;
  target: string;
}

export interface WorkflowWriterInput {
  workspaceRoot: string;
  workflowId: string;
  workflowName: string;
  nodes: ReadonlyArray<WorkflowWriterNode>;
  edges: ReadonlyArray<WorkflowWriterEdge>;
  /**
   * Author-written preconditions (markdown). Written to `workflow/preflight.md`
   * only when non-blank — see `hasPreflightContent`. Absent/blank ⇒ no file, so
   * the reader can't mistake an empty section for "no preconditions exist".
   */
  preflight?: string | null;
}

/** True when preflight has author content worth materializing (non-blank). */
export const hasPreflightContent = (preflight: string | null | undefined): boolean =>
  typeof preflight === 'string' && preflight.trim().length > 0;

export const writeWorkflowDefinition = async (input: WorkflowWriterInput): Promise<void> => {
  // Order by flow (edge) order; orderIndex is deprecated. WorkflowWriterEdge
  // uses source/target, so map to the topo helper's sourceNodeId/targetNodeId.
  const sortedNodes = topoOrderNodes(
    [...input.nodes],
    input.edges.map((e) => ({ sourceNodeId: e.source, targetNodeId: e.target }))
  );

  const definition = {
    workflowId: input.workflowId,
    name: input.workflowName,
    nodes: sortedNodes.map((n) => ({
      id: n.id,
      nodeType: n.nodeType,
      orderIndex: n.orderIndex,
      summary: n.summary,
    })),
    edges: input.edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
    })),
  };

  await fs.writeFile(
    resolveWorkspacePath(input.workspaceRoot, WORKSPACE_PATHS.workflowDefinition),
    `${JSON.stringify(definition, null, 2)}\n`,
    'utf-8',
  );

  const snapshot = {
    workflowId: input.workflowId,
    name: input.workflowName,
    nodes: sortedNodes.map((n) => ({
      id: n.id,
      nodeType: n.nodeType,
      orderIndex: n.orderIndex,
      configJson: n.configJson,
    })),
    edges: input.edges.map((e) => ({ id: e.id, source: e.source, target: e.target })),
  };
  await fs.writeFile(
    resolveWorkspacePath(input.workspaceRoot, WORKSPACE_PATHS.workflowSnapshot),
    `${JSON.stringify(snapshot, null, 2)}\n`,
    'utf-8',
  );

  // Per-node files target distinct paths — write concurrently.
  await Promise.all(
    sortedNodes.map((node) => {
      const filePath = resolveWorkspacePath(
        input.workspaceRoot,
        workflowNodeRelativePath(node.id),
      );
      const body = {
        id: node.id,
        nodeType: node.nodeType,
        orderIndex: node.orderIndex,
        summary: node.summary,
        configJson: node.configJson,
      };
      return fs.writeFile(filePath, `${JSON.stringify(body, null, 2)}\n`, 'utf-8');
    }),
  );

  // Preflight is materialized ONLY when the workflow declares it — a blank/absent
  // value writes no file, so the agent never infers "empty section ⇒ no preconditions".
  if (hasPreflightContent(input.preflight)) {
    const preflight = input.preflight as string;
    await fs.writeFile(
      resolveWorkspacePath(input.workspaceRoot, WORKSPACE_PATHS.workflowPreflight),
      preflight.endsWith('\n') ? preflight : `${preflight}\n`,
      'utf-8',
    );
  }
};
